import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DollarSign, Plus, Trash2, X, CheckCircle, RefreshCw } from 'lucide-react';
import { SwissCard } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;

const CommissionsTab = () => {
  const [data, setData] = useState({ commissions: [], total_owed: 0, total_paid: 0, unpaid_count: 0, this_month: 0 });
  const [drivers, setDrivers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ type: 'driver_cut', driver_name: '', partner_name: '', trip_revenue: 0, commission_pct: 20, amount: 0, date: new Date().toISOString().slice(0, 10), booking_ref: '' });

  const load = () => {
    fetch(`${API}/api/command-center/commissions`).then(r => r.json()).then(setData);
    fetch(`${API}/api/command-center/drivers`).then(r => r.json()).then(d => setDrivers(d.drivers || []));
    fetch(`${API}/api/command-center/affiliate-network`).then(r => r.json()).then(d => setPartners(d.partners || []));
  };
  useEffect(load, []);

  const create = async () => {
    const amt = form.amount || Math.round(form.trip_revenue * (form.commission_pct / 100));
    await fetch(`${API}/api/command-center/commissions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, amount: amt }) });
    setShowForm(false); setForm({ type: 'driver_cut', driver_name: '', partner_name: '', trip_revenue: 0, commission_pct: 20, amount: 0, date: new Date().toISOString().slice(0, 10), booking_ref: '' }); load();
  };

  const markPaid = async (id) => {
    await fetch(`${API}/api/command-center/commissions/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'paid' }) });
    load();
  };

  const del = async (id) => { await fetch(`${API}/api/command-center/commissions/${id}`, { method: 'DELETE' }); load(); };

  const filtered = filter === 'all' ? data.commissions :
    filter === 'unpaid' ? data.commissions.filter(c => c.status !== 'paid') :
    filter === 'drivers' ? data.commissions.filter(c => c.type === 'driver_cut') :
    data.commissions.filter(c => c.type !== 'driver_cut');

  const calcAmount = Math.round((form.trip_revenue || 0) * ((form.commission_pct || 0) / 100));

  return (
    <div data-testid="commissions-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-600" /> Commission Tracker</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="text-xs h-8"><RefreshCw className="h-3 w-3 mr-1" /> Refresh</Button>
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-xs h-8" data-testid="log-commission-btn"><Plus className="h-3 w-3 mr-1" /> Log Commission</Button>
        </div>
      </div>

      {showForm && (
        <SwissCard className="p-4 mb-4">
          <div className="flex justify-between mb-3"><h3 className="text-sm font-bold">Log Commission</h3><button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-gray-400" /></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 block mb-1">Type</label>
              <div className="flex gap-1">
                {[{ v: 'driver_cut', l: 'Driver Cut' }, { v: 'affiliate_payout', l: 'Affiliate Payout' }, { v: 'referral_fee', l: 'Referral Fee' }].map(t => (
                  <button key={t.v} onClick={() => setForm(p => ({ ...p, type: t.v }))}
                    className={`flex-1 h-9 text-[9px] font-bold rounded border ${form.type === t.v ? 'bg-black text-white border-black' : 'border-gray-200'}`}>{t.l}</button>
                ))}
              </div>
            </div>
            {form.type === 'driver_cut' ? (
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Driver</label>
                <Select value={form.driver_name} onValueChange={v => setForm(p => ({ ...p, driver_name: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select driver..." /></SelectTrigger><SelectContent>{drivers.map(d => <SelectItem key={d.driver_id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select>
              </div>
            ) : (
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Partner / Affiliate</label>
                <Select value={form.partner_name} onValueChange={v => setForm(p => ({ ...p, partner_name: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select partner..." /></SelectTrigger><SelectContent>{partners.map(p => <SelectItem key={p.partner_id} value={p.name}>{p.name}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
            <div><label className="text-[10px] text-gray-500 block mb-1">Trip Revenue ($)</label><Input type="number" value={form.trip_revenue || ''} onChange={e => setForm(p => ({ ...p, trip_revenue: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" /></div>
            <div><label className="text-[10px] text-gray-500 block mb-1">Commission %</label><Input type="number" value={form.commission_pct || ''} onChange={e => setForm(p => ({ ...p, commission_pct: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" /></div>
            <div><label className="text-[10px] text-gray-500 block mb-1">Amount Owed ($)</label><Input type="number" value={form.amount || calcAmount || ''} onChange={e => setForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" placeholder={`Auto: ${f(calcAmount)}`} /></div>
            <div><label className="text-[10px] text-gray-500 block mb-1">Date</label><Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="h-9 text-xs" /></div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="text-[10px] text-gray-500 block mb-1">Booking Ref / Notes</label><Input value={form.booking_ref} onChange={e => setForm(p => ({ ...p, booking_ref: e.target.value }))} className="h-9 text-xs" placeholder="BK-XXXXXXXX or description" /></div>
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" onClick={create} className="bg-red-600 hover:bg-red-700 text-xs h-8">Log Commission</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button></div>
        </SwissCard>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-red-600">{f(data.total_owed)}</p><p className="text-[10px] text-gray-500">Total Owed</p></SwissCard>
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-emerald-600">{f(data.total_paid)}</p><p className="text-[10px] text-gray-500">Total Paid</p></SwissCard>
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-amber-600">{data.unpaid_count}</p><p className="text-[10px] text-gray-500">Unpaid Items</p></SwissCard>
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-gray-900">{f(data.this_month)}</p><p className="text-[10px] text-gray-500">This Month</p></SwissCard>
      </div>

      <div className="flex gap-1 mb-3">
        {['all', 'unpaid', 'drivers', 'affiliates'].map(f2 => (
          <button key={f2} onClick={() => setFilter(f2)} className={`px-3 py-1 rounded-full text-[10px] font-bold capitalize ${filter === f2 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{f2}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <SwissCard className="p-8 text-center"><DollarSign className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">No entries in this view.</p></SwissCard>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <SwissCard key={c.commission_id} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${c.type === 'driver_cut' ? 'bg-blue-100 text-blue-700' : c.type === 'affiliate_payout' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}`}>
                      {c.type === 'driver_cut' ? 'Driver' : c.type === 'affiliate_payout' ? 'Affiliate' : 'Referral'}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{c.driver_name || c.partner_name || '—'}</span>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${c.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span>
                  </div>
                  <div className="flex gap-3 text-[10px] text-gray-500 mt-0.5">
                    <span>{c.date}</span>
                    <span>Rev: {f(c.trip_revenue)}</span>
                    <span>{c.commission_pct}%</span>
                    {c.booking_ref && <span>{c.booking_ref}</span>}
                  </div>
                </div>
                <div className="text-right mr-3">
                  <p className="text-sm font-bold text-red-600">{f(c.amount)}</p>
                </div>
                <div className="flex gap-1">
                  {c.status !== 'paid' && <button onClick={() => markPaid(c.commission_id)} className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold flex items-center gap-0.5"><CheckCircle className="h-3 w-3" /> Paid</button>}
                  <button onClick={() => del(c.commission_id)} className="p-1.5 rounded hover:bg-red-100"><Trash2 className="h-3 w-3 text-red-500" /></button>
                </div>
              </div>
            </SwissCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommissionsTab;
